import { createContext, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './services/auth';
import Layout from './components/layout/Layout';
import GettingStarted, { useShowTutorial } from './components/GettingStarted';
import Dashboard from './pages/Dashboard';
import LiveFeed from './pages/LiveFeed';
import Cameras from './pages/Cameras';
import Faces from './pages/Faces';
import Zones from './pages/Zones';
import ProductSetup from './pages/ProductSetup';
import Videos from './pages/Videos';
import Branches from './pages/Branches';
import MediaPlayers from './pages/MediaPlayers';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Downloads from './pages/Downloads';
import Billing from './pages/Billing';
import AdminDashboard from './pages/AdminDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import TvPlayer from './pages/TvPlayer';
import Login from './pages/Login';
import Register from './pages/Register';

// Tutorial context so Settings page can trigger it
const TutorialContext = createContext<{ open: () => void }>({ open: () => {} });
export const useTutorial = () => useContext(TutorialContext);

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  const { isAuthenticated } = useAuthStore();
  const tutorial = useShowTutorial();

  return (
    <TutorialContext.Provider value={{ open: tutorial.open }}>
    {isAuthenticated && <GettingStarted show={tutorial.show} onClose={tutorial.close} />}
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: 'var(--chart-tooltip-bg)',
          color: 'var(--chart-text)',
          border: '1px solid var(--chart-tooltip-border)',
        },
      }}
    />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/tv" element={<TvPlayer />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      {/* Downloads page - show with Layout if authenticated, otherwise handled by Downloads component */}
      <Route
        path="/downloads"
        element={
          isAuthenticated ? (
            <Layout>
              <Downloads />
            </Layout>
          ) : (
            <Downloads />
          )
        }
      />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/live-feed" element={<LiveFeed />} />
                <Route path="/cameras" element={<Cameras />} />
                <Route path="/branches" element={<Branches />} />
                <Route path="/faces" element={<Faces />} />
                <Route path="/zones" element={<Zones />} />
                <Route path="/products" element={<ProductSetup />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/media-players" element={<MediaPlayers />} />
                <Route path="/users" element={<Users />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
    </TutorialContext.Provider>
  );
}

export default App;
